import axios from 'axios';

// Watches API requests to tell whether the server can be reached, and how
// quickly. Phones often report "online" in the sports hall while nothing gets
// through, so real requests are trusted over navigator.onLine.
//   'online'  - requests are coming back promptly
//   'slow'    - a request has taken longer than SLOW_MS
//   'offline' - a request failed without reaching the server

export const SLOW_MS = 3000;
const FAST_MS = 1500;
const RECHECK_MS = 15000;

let status = typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'online';
const listeners = new Set();
let pendingSlow = 0;
let recheckTimer = null;

function set(next) {
  if (next === status) return;
  status = next;
  if (status === 'offline') startRecheck(); else stopRecheck();
  listeners.forEach(fn => fn(status));
}

export const getConnectionStatus = () => status;
export function subscribeConnection(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// While offline, quietly ask the server if it's back
function startRecheck() {
  if (recheckTimer) return;
  recheckTimer = setInterval(() => {
    axios.get('/api/health', { timeout: 8000, connectionCheck: true }).catch(() => {});
  }, RECHECK_MS);
}
function stopRecheck() {
  clearInterval(recheckTimer);
  recheckTimer = null;
}

function onRequest(config) {
  config.connectionStart = Date.now();
  // Mark slow while a request is still waiting, not only once it finishes
  config.connectionTimer = setTimeout(() => {
    config.connectionTimer = null;
    pendingSlow += 1;
    if (status === 'online') set('slow');
  }, SLOW_MS);
  return config;
}

function finish(config) {
  if (!config) return null;
  if (config.connectionTimer) clearTimeout(config.connectionTimer);
  else if (config.connectionStart) pendingSlow = Math.max(0, pendingSlow - 1);
  return config.connectionStart ? Date.now() - config.connectionStart : null;
}

// Used when a request was answered from what's saved on the phone because the
// server couldn't be reached (the request itself never reports an error)
export function markNoAnswer() {
  set('offline');
}

function onResponse(response) {
  const took = finish(response.config);
  if (response.offline) return response; // answered from the phone, says nothing about the server
  if (took != null && took > SLOW_MS) set('slow');
  else if (status === 'offline' || (status === 'slow' && took != null && took < FAST_MS && pendingSlow === 0)) set('online');
  return response;
}

function onError(error) {
  const took = finish(error.config);
  if (axios.isCancel(error)) return Promise.reject(error);
  if (error.response) {
    // The server answered (even with an error), so the connection works
    if (status === 'offline') set(took != null && took > SLOW_MS ? 'slow' : 'online');
  } else if (!error.isRateLimited) {
    set('offline');
  }
  return Promise.reject(error);
}

let installed = false;
export function watchConnection(clients = [axios]) {
  if (installed) return;
  installed = true;
  clients.forEach(client => {
    if (!client?.interceptors) return; // mocked in tests
    client.interceptors.request.use(onRequest);
    client.interceptors.response.use(onResponse, onError);
  });
  window.addEventListener('offline', () => set('offline'));
  // The phone thinks it's back: check with the server rather than trusting it
  window.addEventListener('online', () => {
    axios.get('/api/health', { timeout: 8000 }).catch(() => {});
  });
  if (status === 'offline') startRecheck();
}
